import type {
  StayCleaningInput,
  StayFull,
  StayFullInput,
  StayFullPatch,
  StaySource,
  StaysListingHead,
  StaysPolicy,
} from '../../../../interfaces/stays';
import { calculatedPayout, centsToInput, parseAmount, percentOf } from '../../../../utils/stays-money';
import { daysBetween } from '../../../../function-utils/building-time';
import { isStayDayKey, rangeText } from '../stays-reservations-model';

/**
 * THE STAY FORM, AS PURE RULES (ai-plans/stays plan S4, work package S4.9).
 *
 * One form state is shared by the New stay page and the Edit stay modal. Every
 * text a person types stays TEXT here (amounts included) until the moment a
 * body is built: amounts become integer cents through parseAmount() and never
 * through a float. Dates are building day keys ('2026-10-14') compared as
 * strings and counted with daysBetween — never turned into a JS Date.
 *
 * The words of every local check are the SERVER's words for the same check
 * (lobbero_be src/stays/reservations/validate.js MSG, src/stays/money.js
 * MONEY_MSG), so a field says the same thing whichever side caught it.
 */

export type CleaningKind = 'none' | 'agency' | 'self';
export type CleaningDone = 'scheduled' | 'completed';
export type PayoutMode = 'typed' | 'calculated';

export type MoneyKey =
  | 'gross_cents'
  | 'channel_fee_cents'
  | 'tourist_tax_cents'
  | 'host_payout_cents'
  | 'payment_recorded_cents';

export const MONEY_KEYS: ReadonlyArray<MoneyKey> = [
  'gross_cents',
  'channel_fee_cents',
  'tourist_tax_cents',
  'host_payout_cents',
  'payment_recorded_cents',
];

export interface StayForm {
  lead_first_name: string;
  lead_last_name: string;
  lead_email: string;
  lead_phone: string;
  party_size: number;
  source: StaySource;
  check_in: string;
  check_out: string;
  expected_arrival_time: string;
  expected_departure_time: string;
  gross_cents: string;
  channel_fee_cents: string;
  tourist_tax_cents: string;
  host_payout_cents: string;
  payment_recorded_cents: string;
  /** typed payout only: the host has typed the payout (else it follows gross − fees). */
  payout_edited: boolean;
  cleaning_kind: CleaningKind;
  cleaning_done: CleaningDone;
  cleaning_fee: string;
  cleaning_notes: string;
  notes: string;
}

export type FormKey = keyof StayForm;
export type FieldErrors = Record<string, string>;

/** What the reducer needs to keep derived fields in step. */
export interface FormContext {
  currency: string;
  mode: PayoutMode;
  defaultCleaningFeeCents: number | null;
  /** Independent Stays IS5: a co-host never sees or sends an amount — the server
   *  strips money from what they read and refuses it in what they write
   *  (COHOST_FIELD_NOT_ALLOWED). The form keeps its money keys empty and out of
   *  every body; an agency cleaning takes the listing's default fee. */
  hideMoney?: boolean;
}

export const NAME_MAX = 80;
export const PHONE_MAX = 32;
export const EMAIL_MAX = 254;
export const NOTES_MAX = 1000;
export const CLEANING_NOTES_MAX = 500;
export const MAX_NIGHTS = 90;

export const SOURCE_CHOICES: ReadonlyArray<{ value: StaySource; label: string }> = [
  { value: 'direct', label: 'Direct' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'booking', label: 'Booking.com' },
  { value: 'vrbo', label: 'Vrbo' },
  { value: 'other', label: 'Other' },
];

export const CLEANING_KIND_CHOICES: ReadonlyArray<{ value: CleaningKind; label: string }> = [
  { value: 'none', label: 'Not scheduled' },
  { value: 'agency', label: 'Agency' },
  { value: 'self', label: 'Self' },
];

export const CLEANING_DONE_CHOICES: ReadonlyArray<{ value: CleaningDone; label: string }> = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Cleaned' },
];

/** The order a failed save walks to find the field to focus. */
export const FIELD_ORDER: ReadonlyArray<string> = [
  'lead_first_name',
  'lead_last_name',
  'party_size',
  'source',
  'lead_phone',
  'lead_email',
  'check_in',
  'check_out',
  'expected_arrival_time',
  'expected_departure_time',
  'gross_cents',
  'channel_fee_cents',
  'tourist_tax_cents',
  'host_payout_cents',
  'payment_recorded_cents',
  'cleaning',
  'cleaning_fee',
  'cleaning_notes',
  'notes',
];

/** The server's words (validate.js MSG / money.js MONEY_MSG). */
export const MSG = {
  first: "Enter the lead guest's first name.",
  last: "Enter the lead guest's last name.",
  nameLong: 'Use at most 80 characters.',
  email: 'Enter a valid email address.',
  phone: 'Use digits, spaces, + and - only (at most 32).',
  checkIn: 'Choose the check-in date.',
  checkOut: 'Choose the check-out date.',
  order: 'Check-out must be after check-in.',
  long: 'A stay can be at most 90 nights.',
  afterToday: 'Check-out must be after today (building time).',
  needsGross: 'Enter the total paid by the guest first.',
  feesOver: "The channel fee and tourist tax can't add up to more than the total.",
  paymentOver: "The amount recorded as paid can't be more than the total.",
  cleaningFee: 'Enter the agency fee.',
  cleaningNotes: 'Use at most 500 characters.',
  notes: 'Use at most 1000 characters.',
} as const;

const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[0-9 +\-()]{3,32}$/;

export const payoutModeOf = (policy?: StaysPolicy | null): PayoutMode =>
  policy && policy.host_payout === 'calculated' ? 'calculated' : 'typed';

export const contextOf = (listing: StaysListingHead, policy?: StaysPolicy | null, hideMoney = false): FormContext => ({
  currency: listing.currency,
  mode: payoutModeOf(policy),
  defaultCleaningFeeCents: hideMoney ? null : listing.default_cleaning_fee_cents ?? null,
  ...(hideMoney ? { hideMoney: true } : {}),
});

/** Parsed cents of a money text, or null (empty OR not an amount). */
export const centsOf = (text: string, currency: string): number | null => parseAmount(text, currency).cents;

/** gross − fee − tax of the typed amounts, as input text ('' without a gross). */
export const prefillPayout = (form: Pick<StayForm, 'gross_cents' | 'channel_fee_cents' | 'tourist_tax_cents'>, currency: string): string =>
  centsToInput(
    calculatedPayout(
      centsOf(form.gross_cents, currency),
      centsOf(form.channel_fee_cents, currency),
      centsOf(form.tourist_tax_cents, currency)
    ),
    currency
  );

export const emptyForm = (ctx: FormContext): StayForm => ({
  lead_first_name: '',
  lead_last_name: '',
  lead_email: '',
  lead_phone: '',
  party_size: 1,
  source: 'direct',
  check_in: '',
  check_out: '',
  expected_arrival_time: '',
  expected_departure_time: '',
  gross_cents: '',
  channel_fee_cents: '',
  tourist_tax_cents: '',
  host_payout_cents: '',
  payment_recorded_cents: '',
  payout_edited: false,
  cleaning_kind: 'none',
  cleaning_done: 'scheduled',
  cleaning_fee: centsToInput(ctx.defaultCleaningFeeCents, ctx.currency),
  cleaning_notes: '',
  notes: '',
});

/** The form a stored stay opens with. */
export const formFromStay = (stay: StayFull, ctx: FormContext): StayForm => {
  const cur = ctx.currency;
  // A co-host's stay carries no `money` (IS5): every amount opens empty.
  const money: Partial<StayFull['money']> = stay.money || {};
  const typed = money.typed_payout_cents ?? null;
  const base = {
    gross_cents: centsToInput(money.gross_cents, cur),
    channel_fee_cents: centsToInput(money.channel_fee_cents, cur),
    tourist_tax_cents: centsToInput(money.tourist_tax_cents, cur),
  };
  const cleaning = stay.cleaning;
  const kind: CleaningKind = cleaning.status === 'unscheduled' || !cleaning.type ? 'none' : cleaning.type;

  return {
    lead_first_name: stay.lead.first_name || '',
    lead_last_name: stay.lead.last_name || '',
    lead_email: stay.lead.email || '',
    lead_phone: stay.lead.phone || '',
    party_size: stay.party_size,
    source: stay.source,
    check_in: stay.check_in,
    check_out: stay.check_out,
    expected_arrival_time: stay.expected_arrival_time || '',
    expected_departure_time: stay.expected_departure_time || '',
    ...base,
    host_payout_cents: ctx.mode === 'typed' && typed !== null ? centsToInput(typed, cur) : prefillPayout(base, cur),
    payout_edited: ctx.mode === 'typed' && typed !== null,
    payment_recorded_cents: centsToInput(money.payment_recorded_cents, cur),
    cleaning_kind: kind,
    cleaning_done: cleaning.status === 'completed' ? 'completed' : 'scheduled',
    cleaning_fee:
      kind === 'agency' ? centsToInput(cleaning.fee_cents, cur) : centsToInput(ctx.defaultCleaningFeeCents, cur),
    cleaning_notes: cleaning.notes || '',
    notes: stay.notes || '',
  };
};

/**
 * One edit, with the derived fields kept in step: typing the payout marks it
 * the host's own; until then it follows gross − fee − tax. Choosing Agency
 * with no fee fills the listing's default.
 */
export const applyPatch = (form: StayForm, patch: Partial<StayForm>, ctx: FormContext): StayForm => {
  const next: StayForm = { ...form, ...patch };
  if (patch.host_payout_cents !== undefined && patch.payout_edited === undefined) next.payout_edited = true;
  const moved =
    patch.gross_cents !== undefined ||
    patch.channel_fee_cents !== undefined ||
    patch.tourist_tax_cents !== undefined ||
    patch.payout_edited === false;
  if (!next.payout_edited && moved) next.host_payout_cents = prefillPayout(next, ctx.currency);
  if (patch.cleaning_kind === 'agency' && next.cleaning_fee.trim() === '') {
    next.cleaning_fee = centsToInput(ctx.defaultCleaningFeeCents, ctx.currency);
  }

  return next;
};

/** Nights between two day keys, or null while either is missing or out of order. */
export const nightsOf = (checkIn: string, checkOut: string): number | null => {
  if (!isStayDayKey(checkIn) || !isStayDayKey(checkOut)) return null;
  const n = daysBetween(checkIn, checkOut);

  return Number.isFinite(n) && n > 0 ? n : null;
};

/** The error of one money field on its own (on blur). */
export const amountError = (text: string, currency: string): string | null => parseAmount(text, currency).error;

/** The money rules of the whole form: each amount, then the rules between them. */
export const moneyErrors = (form: StayForm, ctx: FormContext): FieldErrors => {
  const errors: FieldErrors = {};
  if (ctx.hideMoney) return errors;
  const cents: Partial<Record<MoneyKey, number | null>> = {};
  for (const key of MONEY_KEYS) {
    if (key === 'host_payout_cents' && (ctx.mode === 'calculated' || !form.payout_edited)) continue;
    const parsed = parseAmount(form[key], ctx.currency);
    if (parsed.error) errors[key] = parsed.error;
    else cents[key] = parsed.cents;
  }
  if (Object.keys(errors).length) return errors;
  const gross = cents.gross_cents ?? null;
  const fee = cents.channel_fee_cents ?? null;
  const tax = cents.tourist_tax_cents ?? null;
  const paid = cents.payment_recorded_cents ?? null;
  if (gross === null) {
    if (fee !== null) errors.channel_fee_cents = MSG.needsGross;
    if (tax !== null) errors.tourist_tax_cents = MSG.needsGross;
    if (paid !== null) errors.payment_recorded_cents = MSG.needsGross;
  } else {
    if ((fee || 0) + (tax || 0) > gross) errors[fee !== null ? 'channel_fee_cents' : 'tourist_tax_cents'] = MSG.feesOver;
    if (paid !== null && paid > gross) errors.payment_recorded_cents = MSG.paymentOver;
  }

  return errors;
};

export interface ValidateOptions {
  /** The building's today: a checked-in stay's check-out must be after it. */
  checkOutAfter?: string | null;
}

/** Every local check at once (the server's words). */
export const validateForm = (form: StayForm, ctx: FormContext, opts: ValidateOptions = {}): FieldErrors => {
  const e: FieldErrors = {};
  const first = form.lead_first_name.trim();
  const last = form.lead_last_name.trim();
  if (!first) e.lead_first_name = MSG.first;
  else if (first.length > NAME_MAX) e.lead_first_name = MSG.nameLong;
  if (!last) e.lead_last_name = MSG.last;
  else if (last.length > NAME_MAX) e.lead_last_name = MSG.nameLong;
  const email = form.lead_email.trim();
  if (email && !EMAIL_RE.test(email)) e.lead_email = MSG.email;
  const phone = form.lead_phone.trim();
  if (phone && !PHONE_RE.test(phone)) e.lead_phone = MSG.phone;

  if (!isStayDayKey(form.check_in)) e.check_in = MSG.checkIn;
  if (!isStayDayKey(form.check_out)) e.check_out = MSG.checkOut;
  else if (isStayDayKey(form.check_in) && form.check_out <= form.check_in) e.check_out = MSG.order;
  else if ((nightsOf(form.check_in, form.check_out) || 0) > MAX_NIGHTS) e.check_out = MSG.long;
  else if (opts.checkOutAfter && form.check_out <= opts.checkOutAfter) e.check_out = MSG.afterToday;

  Object.assign(e, moneyErrors(form, ctx));

  if (form.cleaning_kind === 'agency' && !ctx.hideMoney) {
    if (form.cleaning_fee.trim() === '') e.cleaning_fee = MSG.cleaningFee;
    else {
      const fee = amountError(form.cleaning_fee, ctx.currency);
      if (fee) e.cleaning_fee = fee;
    }
  }
  if (form.cleaning_notes.length > CLEANING_NOTES_MAX) e.cleaning_notes = MSG.cleaningNotes;
  if (form.notes.length > NOTES_MAX) e.notes = MSG.notes;

  return e;
};

/** The cleaning object of a body. On a patch, emptied notes are sent as null. */
export const cleaningInput = (form: StayForm, ctx: FormContext, forPatch: boolean): StayCleaningInput => {
  const notes = form.cleaning_notes.trim();
  const notesPart = notes ? { notes } : forPatch ? { notes: null } : {};
  if (form.cleaning_kind === 'none') return { status: 'unscheduled', ...notesPart };
  if (form.cleaning_kind === 'self') return { status: form.cleaning_done, type: 'self', ...notesPart };

  return {
    status: form.cleaning_done,
    type: 'agency',
    // A co-host sends no fee: the server takes the listing's default (IS5).
    ...(ctx.hideMoney ? {} : { fee_cents: centsOf(form.cleaning_fee, ctx.currency) }),
    ...notesPart,
  };
};

/** The payout the host typed, under the typed switch only (undefined = leave it out). */
const typedPayout = (form: StayForm, ctx: FormContext): number | null | undefined => {
  if (ctx.mode !== 'typed' || !form.payout_edited) return undefined;

  return centsOf(form.host_payout_cents, ctx.currency);
};

/** POST /stays/listings/:id/reservations — no check-in link yet. */
export const buildCreateInput = (form: StayForm, ctx: FormContext): StayFullInput => {
  const email = form.lead_email.trim();
  const phone = form.lead_phone.trim();
  const notes = form.notes.trim();
  const input: StayFullInput = {
    check_in: form.check_in,
    check_out: form.check_out,
    lead_first_name: form.lead_first_name.trim(),
    lead_last_name: form.lead_last_name.trim(),
    party_size: form.party_size,
    source: form.source,
    ...(email ? { lead_email: email } : {}),
    ...(phone ? { lead_phone: phone } : {}),
    ...(form.expected_arrival_time ? { expected_arrival_time: form.expected_arrival_time } : {}),
    ...(form.expected_departure_time ? { expected_departure_time: form.expected_departure_time } : {}),
  };
  if (!ctx.hideMoney) {
    for (const key of ['gross_cents', 'channel_fee_cents', 'tourist_tax_cents', 'payment_recorded_cents'] as const) {
      const cents = centsOf(form[key], ctx.currency);
      if (cents !== null) input[key] = cents;
    }
    const payout = typedPayout(form, ctx);
    if (payout !== undefined && payout !== null) input.host_payout_cents = payout;
  }
  input.cleaning = cleaningInput(form, ctx, false);
  if (notes) input.notes = notes;
  input.issue_link = false;

  return input;
};

const SIMPLE_KEYS = [
  'lead_first_name',
  'lead_last_name',
  'lead_email',
  'lead_phone',
  'party_size',
  'source',
  'check_in',
  'check_out',
  'expected_arrival_time',
  'expected_departure_time',
  'notes',
] as const;

const PLAIN_MONEY = ['gross_cents', 'channel_fee_cents', 'tourist_tax_cents', 'payment_recorded_cents'] as const;
const CLEANING_KEYS = ['cleaning_kind', 'cleaning_done', 'cleaning_fee', 'cleaning_notes'] as const;

/** The payout as a fact to compare: the host's own text, or "follows the amounts". */
const payoutFact = (form: StayForm, ctx: FormContext): string =>
  ctx.mode === 'typed' && form.payout_edited ? `typed:${form.host_payout_cents.trim()}` : 'calculated';

const cleaningFact = (form: StayForm): string =>
  [
    form.cleaning_kind,
    form.cleaning_kind === 'none' ? '' : form.cleaning_done,
    form.cleaning_kind === 'agency' ? form.cleaning_fee.trim() : '',
    form.cleaning_notes.trim(),
  ].join('|');

const same = (a: string | number, b: string | number): boolean =>
  typeof a === 'string' && typeof b === 'string' ? a.trim() === b.trim() : a === b;

/** The fields a person changed from `base` (what the server holds). */
export const changedKeys = (form: StayForm, base: StayForm, ctx: FormContext): FormKey[] => {
  const out: FormKey[] = [];
  for (const key of SIMPLE_KEYS) if (!same(form[key], base[key])) out.push(key);
  for (const key of PLAIN_MONEY) if (!same(form[key], base[key])) out.push(key);
  if (payoutFact(form, ctx) !== payoutFact(base, ctx)) out.push('host_payout_cents');
  if (cleaningFact(form) !== cleaningFact(base)) {
    for (const key of CLEANING_KEYS) if (!same(form[key], base[key])) out.push(key);
    if (!out.some(k => (CLEANING_KEYS as ReadonlyArray<string>).includes(k))) out.push('cleaning_kind');
  }

  return out;
};

/**
 * A stale save (STAY_CHANGED): the server's copy underneath, the person's
 * changed fields on top. The payout prefill follows the merged amounts.
 */
export const carryEdits = (form: StayForm, fresh: StayForm, keys: ReadonlyArray<FormKey>, ctx: FormContext): StayForm => {
  const merged: StayForm = { ...fresh };
  const put = <K extends FormKey>(key: K) => {
    merged[key] = form[key];
  };
  for (const key of keys) {
    put(key);
    if (key === 'host_payout_cents') put('payout_edited');
  }
  if (!merged.payout_edited) merged.host_payout_cents = prefillPayout(merged, ctx.currency);

  return merged;
};

const textOrNull = (text: string): string | null => {
  const t = text.trim();

  return t ? t : null;
};

/** PATCH /stays/reservations/:id — ONLY the changed fields, plus the version. */
export const buildPatch = (form: StayForm, base: StayForm, ctx: FormContext, version: number): StayFullPatch => {
  const changed = new Set<string>(changedKeys(form, base, ctx));
  const patch: StayFullPatch = { version };
  if (changed.has('lead_first_name')) patch.lead_first_name = form.lead_first_name.trim();
  if (changed.has('lead_last_name')) patch.lead_last_name = form.lead_last_name.trim();
  if (changed.has('lead_email')) patch.lead_email = textOrNull(form.lead_email);
  if (changed.has('lead_phone')) patch.lead_phone = textOrNull(form.lead_phone);
  if (changed.has('party_size')) patch.party_size = form.party_size;
  if (changed.has('source')) patch.source = form.source;
  if (changed.has('check_in')) patch.check_in = form.check_in;
  if (changed.has('check_out')) patch.check_out = form.check_out;
  if (changed.has('expected_arrival_time')) patch.expected_arrival_time = textOrNull(form.expected_arrival_time);
  if (changed.has('expected_departure_time')) patch.expected_departure_time = textOrNull(form.expected_departure_time);
  if (changed.has('notes')) patch.notes = textOrNull(form.notes);
  if (!ctx.hideMoney) for (const key of PLAIN_MONEY) if (changed.has(key)) patch[key] = centsOf(form[key], ctx.currency);
  if (changed.has('host_payout_cents') && ctx.mode === 'typed' && !ctx.hideMoney) {
    patch.host_payout_cents = form.payout_edited ? centsOf(form.host_payout_cents, ctx.currency) : null;
  }
  if (CLEANING_KEYS.some(k => changed.has(k))) patch.cleaning = cleaningInput(form, ctx, true);

  return patch;
};

/** The payout the form shows: the host's own (typed switch), else gross − fees. */
export const effectivePayout = (form: StayForm, ctx: FormContext): number | null => {
  if (ctx.mode === 'typed' && form.payout_edited) return centsOf(form.host_payout_cents, ctx.currency);

  return calculatedPayout(
    centsOf(form.gross_cents, ctx.currency),
    centsOf(form.channel_fee_cents, ctx.currency),
    centsOf(form.tourist_tax_cents, ctx.currency)
  );
};

/** "Fee 15 % · Payout 82 %" — nothing without a total. */
export const percentLine = (form: StayForm, ctx: FormContext): string => {
  const gross = centsOf(form.gross_cents, ctx.currency);
  if (gross === null || gross <= 0) return '';
  const parts: string[] = [];
  const fee = percentOf(centsOf(form.channel_fee_cents, ctx.currency), gross);
  if (fee !== null) parts.push(`Fee ${fee} %`);
  const payout = percentOf(effectivePayout(form, ctx), gross);
  if (payout !== null) parts.push(`Payout ${payout} %`);

  return parts.join(' · ');
};

/** "Total 420.00 · Channel fee 63.00 (15 %) · Tourist tax 12.00 · Payout 345.00 (82 %)". */
export const breakdownStrip = (form: StayForm, ctx: FormContext): string => {
  const cur = ctx.currency;
  const gross = centsOf(form.gross_cents, cur);
  const fee = centsOf(form.channel_fee_cents, cur);
  const tax = centsOf(form.tourist_tax_cents, cur);
  const payout = effectivePayout(form, ctx);
  const pct = (part: number | null) => {
    const p = percentOf(part, gross);

    return p === null ? '' : ` (${p} %)`;
  };
  const parts: string[] = [];
  if (gross !== null) parts.push(`Total ${centsToInput(gross, cur)}`);
  if (fee !== null) parts.push(`Channel fee ${centsToInput(fee, cur)}${pct(fee)}`);
  if (tax !== null) parts.push(`Tourist tax ${centsToInput(tax, cur)}`);
  if (payout !== null) parts.push(`Payout ${centsToInput(payout, cur)}${pct(payout)}`);

  return parts.join(' · ');
};

/** A 409 overlap / closed-nights refusal, as one sentence naming the clash. */
export const conflictSentence = (code: string | null, conflict: Record<string, unknown> | null): string | null => {
  const c = conflict || {};
  if (code === 'STAY_OVERLAP') {
    return isStayDayKey(c.check_in) && isStayDayKey(c.check_out)
      ? `These dates overlap another stay (${rangeText(c.check_in, c.check_out)}).`
      : 'These dates overlap another stay.';
  }
  if (code === 'STAY_BLOCKED') {
    const note = typeof c.note === 'string' && c.note.trim() ? c.note.trim() : '';
    const span = isStayDayKey(c.start_date) && isStayDayKey(c.end_date) ? rangeText(c.start_date, c.end_date) : '';
    const inner = [span, note].filter(Boolean).join(' · ');

    return inner ? `These dates include closed nights (${inner}).` : 'These dates include closed nights.';
  }

  return null;
};

/** Move the focus to a field after a failed save: the element, or the first
 *  focusable thing inside it (a radio group, a stepper, the date picker). */
export const focusField = (id: string): void => {
  window.setTimeout(() => {
    const el = document.getElementById(id);
    if (!el) return;
    if ((el.tabIndex >= 0 || el.hasAttribute('tabindex')) && !(el as HTMLInputElement).disabled) {
      el.focus();

      return;
    }
    const inside = ['[aria-checked="true"]', '[tabindex="0"]', 'input:not([disabled])', 'select', 'textarea', 'button:not([disabled])']
      .map(selector => el.querySelector<HTMLElement>(selector))
      .find(Boolean);
    if (inside) inside.focus();
  }, 0);
};

/** The first key of `errors` in form order (the server's own `field` first). */
export const firstErrorKey = (errors: FieldErrors, preferred?: string | null): string | null => {
  if (preferred && errors[preferred]) return preferred;

  return FIELD_ORDER.find(key => errors[key]) || Object.keys(errors)[0] || null;
};
